/**
 * Impact notice for object-template attribute changes.
 *
 * When an object template already has custom objects, a change to its
 * attributes changes all of them. Before such a change is saved the UI tells the
 * user how many custom objects it affects:
 *
 *  - delete an attribute            -> warning (its data is removed from every custom object)
 *  - change the datatype            -> warning
 *  - change "Required" (is_nullable) -> warning (the attribute becomes required / optional)
 *  - add an attribute               -> info
 *
 * Other edits (description, hidden, searchable, sort order, ...) show nothing.
 * Nothing is shown when the template has no custom objects.
 *
 * This module holds the pure logic (classification + wording); the dialog and
 * the count lookup live in objectTemplateAttributeImpactDialog.tsx.
 */

export type AttributeImpactKind = "add" | "delete" | "datatype-change" | "required-change";

export interface AttributeImpactChange {
    kind: AttributeImpactKind;
    attributeName?: string;
    /** For a required-change: true when the attribute becomes required (not nullable). */
    becomesRequired?: boolean;
}

interface AttributeValues {
    name?: string | null;
    datatype?: string | null;
    is_nullable?: boolean | null;
}

/**
 * Classify an edit of an EXISTING attribute. Returns the data-affecting change
 * (datatype first, then nullability), or null when the edit does not affect the
 * custom objects' data.
 */
export const classifyAttributeUpdate = (
    before: AttributeValues | null | undefined,
    after: AttributeValues | null | undefined
): AttributeImpactChange | null => {
    if (!before || !after) {
        return null;
    }
    const attributeName = after.name || before.name || undefined;
    if (before.datatype && after.datatype && before.datatype !== after.datatype) {
        return { kind: "datatype-change", attributeName };
    }
    if (
        after.is_nullable !== undefined &&
        before.is_nullable !== undefined &&
        Boolean(before.is_nullable) !== Boolean(after.is_nullable)
    ) {
        return { kind: "required-change", attributeName, becomesRequired: !after.is_nullable };
    }
    return null;
};

/** A warning outranks an info notice: any non-"add" change makes the dialog a warning. */
export const isWarningImpact = (changes: AttributeImpactChange[]) =>
    changes.some((change) => change.kind !== "add");

const pluralCustomObjects = (count: number) =>
    count === 1 ? "1 custom object" : `${count} custom objects`;

const quoted = (name?: string) => (name ? `"${name}"` : "this attribute");

export const describeAttributeImpactChange = (change: AttributeImpactChange): string => {
    switch (change.kind) {
        case "add":
            return `Adds the attribute ${quoted(change.attributeName)}.`;
        case "delete":
            return `Deletes the attribute ${quoted(change.attributeName)} and its value in every custom object.`;
        case "datatype-change":
            return `Changes the datatype of ${quoted(change.attributeName)}.`;
        case "required-change":
            return change.becomesRequired
                ? `Makes ${quoted(change.attributeName)} required: it cannot be empty.`
                : `Makes ${quoted(change.attributeName)} optional: it can be empty.`;
        default:
            return "";
    }
};

export interface AttributeImpactMessage {
    title: string;
    summary: string;
    details: string[];
    confirmText: string;
    confirmVariation: "primary" | "danger";
    isWarning: boolean;
}

/**
 * Build the dialog wording for a set of changes on one template that has
 * `count` custom objects.
 */
export const buildAttributeImpactMessage = (
    changes: AttributeImpactChange[],
    count: number
): AttributeImpactMessage => {
    const isWarning = isWarningImpact(changes);
    const onlyDeletes = changes.length > 0 && changes.every((c) => c.kind === "delete");
    const onlyAdds = changes.length > 0 && changes.every((c) => c.kind === "add");
    const objects = pluralCustomObjects(count);
    const these = count === 1 ? "it" : `all ${count} of them`;

    let title = "This change affects existing custom objects";
    let confirmText = "Save";
    if (onlyDeletes) {
        title = changes.length === 1 ? "Delete attribute?" : "Delete attributes?";
        confirmText = changes.length === 1 ? "Delete attribute" : "Delete attributes";
    } else if (onlyAdds) {
        title = "New attribute on existing custom objects";
        confirmText = changes.length === 1 ? "Add attribute" : "Add attributes";
    }

    const summary = `This template has ${objects}. This change affects ${these}.`;

    return {
        title,
        summary,
        details: changes.map(describeAttributeImpactChange),
        confirmText,
        confirmVariation: onlyDeletes ? "danger" : "primary",
        isWarning,
    };
};

/** Rejection marker used when the user cancels the impact dialog. */
export class AttributeImpactCancelled extends Error {
    constructor() {
        super("Attribute change cancelled");
        this.name = "AttributeImpactCancelled";
    }
}

export const isAttributeImpactCancelled = (error: unknown): boolean =>
    error instanceof AttributeImpactCancelled;
